import { type FC, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";

import MainLayout from "@/pages/layouts/MainLayout";
import { DeathCertModal } from "@/pages/onboarding/components/screens/DeathCertModal";
import { CATEGORY_BG, MODULE_IMAGES } from "@/shared/constants/estateEssentials";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { usePerson } from "@/shared/hooks/useAppContext";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { useHasDeathCert } from "@/shared/hooks/useHasDeathCert";

import { useModuleDetail } from "./hooks/useModuleDetail";
import { EssentialsActions } from "./components/EssentialsActions";
import { GuideFormModal } from "./components/GuideFormModal";
import { GuideRenderer } from "./components/GuideRenderer";
import { InlineFormPage } from "./components/InlineFormPage";

function ModuleDetailBreadcrumb({ moduleName }: { moduleName: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-2 text-sm">
      <button
        type="button"
        onClick={() => navigate(POST_LOSS_ROUTES.ESTATE_ESSENTIALS)}
        className="p-0 text-sm text-secondary hover:underline"
      >
        Important to-dos
      </button>
      <span className="text-sm text-primary">/</span>
      <span className="text-sm text-primary">{moduleName}</span>
    </div>
  );
}

const GUIDE_DEATH_CERT_TASKS = new Set<string>([]);

export const ModuleDetailPage: FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const person = usePerson();
  const estateId = person?.estate_id;
  const { tier } = usePaymentTier(estateId);
  const isPremium = hasMinTier(tier, "basic");

  const {
    module,
    formOpen,
    setFormOpen,
    formData,
    saveError,
    guideMarkdown,
    deceasedData,
    userData,
    relationship,
    isVehicleModule,
    vehicleItems,
    isComplete,
    handleFormOpenChange,
    handleFormSubmit,
    handleVehicleChange,
    handleMarkComplete,
  } = useModuleDetail(moduleId);

  const requiresDeathCert = !!module?.taskId && GUIDE_DEATH_CERT_TASKS.has(module.taskId);
  const { hasDeathCert, setHasDeathCert } = useHasDeathCert(estateId);
  const [deathCertModalOpen, setDeathCertModalOpen] = useState(false);

  const handleEditWithDeathCert = useCallback(() => {
    if (requiresDeathCert && !hasDeathCert) {
      setDeathCertModalOpen(true);
    } else {
      setFormOpen(true);
    }
  }, [requiresDeathCert, hasDeathCert, setFormOpen]);

  if (!module || !isPremium) {
    navigate(POST_LOSS_ROUTES.ESTATE_ESSENTIALS, { replace: true });
    return null;
  }

  const displayName =
    module.nameTemplate && deceasedData.displayName
      ? module.nameTemplate.replace("{{ firstName }}", deceasedData.displayName)
      : module.name;

  if (module.renderMode === "form") {
    return (
      <MainLayout
        header={<ModuleDetailBreadcrumb moduleName={displayName} />}
        actions={<EssentialsActions />}
      >
        <div>
          <InlineFormPage module={module} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      header={<ModuleDetailBreadcrumb moduleName={displayName} />}
      actions={<EssentialsActions />}
    >
      <div>
        {saveError && (
          <div className="mb-4 rounded-lg border border-error bg-white p-4 text-sm text-error">
            {saveError}
          </div>
        )}
        {guideMarkdown && (
          <GuideRenderer
            markdown={guideMarkdown}
            moduleName={displayName}
            deceasedFirstName={deceasedData.displayName}
            deceasedFullName={deceasedData.fullName}
            requestorFullName={userData.fullName}
            onEdit={requiresDeathCert ? handleEditWithDeathCert : () => setFormOpen(true)}
            illustration={MODULE_IMAGES[module.id]}
            heroBg={CATEGORY_BG[module.category]}
            vehicleItems={isVehicleModule ? vehicleItems : undefined}
            selectedVehicleId={
              isVehicleModule
                ? vehicleItems.find((v) => String(v.id) === formData.selected_vehicle_id)
                  ? formData.selected_vehicle_id
                  : vehicleItems[0]
                    ? String(vehicleItems[0].id)
                    : undefined
                : undefined
            }
            onVehicleChange={isVehicleModule ? handleVehicleChange : undefined}
            isComplete={isComplete}
            onMarkComplete={handleMarkComplete}
          />
        )}
      </div>

      <GuideFormModal
        open={formOpen}
        onOpenChange={handleFormOpenChange}
        module={module}
        initialData={formData}
        onSubmit={handleFormSubmit}
        deceasedData={deceasedData}
        relationship={relationship}
        vehicleItems={isVehicleModule ? vehicleItems : undefined}
      />

      {requiresDeathCert && estateId && (
        <DeathCertModal
          isOpen={deathCertModalOpen}
          onOpenChange={setDeathCertModalOpen}
          estateId={estateId}
          firstName={deceasedData.displayName}
          onUploaded={() => {
            setHasDeathCert();
            setDeathCertModalOpen(false);
            setFormOpen(true);
          }}
          onSkip={() => setDeathCertModalOpen(false)}
        />
      )}
    </MainLayout>
  );
};
